'use client';

import { ThemeProvider as NextThemesProvider } from 'next-themes';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const isEmbed = pathname?.startsWith('/embed');
  const [embedTheme, setEmbedTheme] = useState<'light' | 'dark'>(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return params.get('theme') === 'dark' ? 'dark' : 'light';
    }
    return 'light';
  });

  useEffect(() => {
    if (!isEmbed) return;
    const params = new URLSearchParams(window.location.search);
    const urlTheme = params.get('theme');
    if (urlTheme === 'dark' || urlTheme === 'light') {
      setEmbedTheme(urlTheme);
    }

    const handleMessage = (event: MessageEvent) => {
      if (
        event.data?.type === 'ONEEYE_THEME_CHANGE' &&
        (event.data.theme === 'light' || event.data.theme === 'dark')
      ) {
        setEmbedTheme(event.data.theme);
      }
    };
    window.addEventListener('message', handleMessage);

    if (window.parent && window.parent !== window) {
      window.parent.postMessage({ type: 'ONEEYE_GET_THEME' }, '*');
    }

    return () => window.removeEventListener('message', handleMessage);
  }, [isEmbed]);

  return (
    <NextThemesProvider
      attribute="class"
      defaultTheme={isEmbed ? embedTheme : 'system'}
      enableSystem={!isEmbed}
      forcedTheme={isEmbed ? embedTheme : undefined}
      storageKey={isEmbed ? 'embed-theme' : 'theme'}
    >
      {children}
    </NextThemesProvider>
  );
}
